// The eval case inbox pages' logic (TOM-140, ADR 0233): the queue's URL
// state and links, the labels it shows, the draft form and the bodies it
// sends, the checks that say why a draft cannot be accepted yet, where an
// accepted case will go, and the consent form's basis. Pure: tested in
// inbox.test.ts.
//
// The server decides. The draft checks and the routing preview here mirror
// the contract (cloud/src/core/neo/eval/inbox-contract.ts) so the form can say
// what is missing before a request; the server's answer (its `draftIssues`,
// `routeProblem` and refusals) is the one that holds.

import {
  EVAL_CANDIDATE_SOURCES,
  EVAL_CASE_EXPECTS,
  EVAL_CASE_KINDS,
  EVAL_CASE_TARGET_AGENTS,
  EVAL_GATE_QUESTIONS,
  EVAL_TRIAGE_STATES,
  type AdminBoatRow,
  type AdminUserRow,
  type EvalCandidateDetail,
  type EvalCandidateInCorpus,
  type EvalCandidateSource,
  type EvalCandidateSummary,
  type EvalCaseData,
  type EvalCaseDraft,
  type EvalCaseExpect,
  type EvalCaseExportFile,
  type EvalCaseKind,
  type EvalCaseTargetAgent,
  type EvalConsentRevokeReason,
  type EvalDraftSaveBody,
  type EvalGateDisagreementInfo,
  type EvalGateQuestion,
  type EvalMomentOption,
  type EvalTriageDecision,
  type EvalTriageState,
  type GateOutcome,
} from './inboxTypes';
import { formatDate } from '../format';
import { EXPERT_TEXT, expertName } from './guide';

// ----- The queue's URL state ---------------------------------------------------------

/** The windows the queue offers, in days. The server's default is 90. */
export const QUEUE_WINDOWS = [7, 30, 90, 365] as const;
export const QUEUE_DEFAULT_DAYS = 90;
export const QUEUE_PAGE_SIZE = 50;

export interface EvalQueueFilter {
  days: number;
  state?: EvalTriageState;
  source?: EvalCandidateSource;
  /** The agent a candidate is for (TOM-533); Neo takes every one not given an expert. */
  agent?: EvalCaseTargetAgent;
}

function oneOf<T extends string>(list: readonly T[], v: string | null): T | undefined {
  return v !== null && (list as readonly string[]).includes(v) ? (v as T) : undefined;
}

/** The queue's filter, read from the page URL. Unknown or malformed values drop out. */
export function queueFilterFromSearch(search: URLSearchParams): EvalQueueFilter {
  const raw = Number(search.get('days'));
  const days = (QUEUE_WINDOWS as readonly number[]).includes(raw) ? raw : QUEUE_DEFAULT_DAYS;
  const filter: EvalQueueFilter = { days };
  const state = oneOf(EVAL_TRIAGE_STATES, search.get('state'));
  if (state !== undefined) filter.state = state;
  const source = oneOf(EVAL_CANDIDATE_SOURCES, search.get('source'));
  if (source !== undefined) filter.source = source;
  const agent = oneOf(EVAL_CASE_TARGET_AGENTS, search.get('agent'));
  if (agent !== undefined) filter.agent = agent;
  return filter;
}

/** The page URL's search for a filter; the default window stays out of it. */
export function queueFilterSearch(filter: EvalQueueFilter): string {
  const params = new URLSearchParams();
  if (filter.days !== QUEUE_DEFAULT_DAYS) params.set('days', String(filter.days));
  if (filter.state !== undefined) params.set('state', filter.state);
  if (filter.source !== undefined) params.set('source', filter.source);
  if (filter.agent !== undefined) params.set('agent', filter.agent);
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

/** The query GET /v1/admin/evals/cases takes for a filter and a page. */
export function queueQuery(filter: EvalQueueFilter, cursor?: string | null): string {
  const params = new URLSearchParams();
  params.set('days', String(filter.days));
  if (filter.state !== undefined) params.set('state', filter.state);
  if (filter.source !== undefined) params.set('source', filter.source);
  if (filter.agent !== undefined) params.set('targetAgent', filter.agent);
  params.set('limit', String(QUEUE_PAGE_SIZE));
  if (cursor !== undefined && cursor !== null) params.set('cursor', cursor);
  return `?${params.toString()}`;
}

// ----- Links -----------------------------------------------------------------------------

/** A candidate's identity for the routes: its row once it has one, else its key. */
export function candidateRef(c: Pick<EvalCandidateSummary, 'rowId' | 'key'>): string {
  return c.rowId ?? c.key;
}

export function candidateHref(c: Pick<EvalCandidateSummary, 'rowId' | 'key'>): string {
  return `/admin/evals/cases/${encodeURIComponent(candidateRef(c))}`;
}

export const CONSENTS_HREF = '/admin/evals/cases/consents';
export const CASES_HREF = '/admin/evals/cases';

/**
 * The audited transcript of a turn candidate, scrolled to the turn. Opening it
 * asks for a reason and writes a fail-closed audit row (ADR 0119). Null for a
 * refusal or a candidate whose turn was erased.
 */
export function transcriptHref(
  c: Pick<EvalCandidateSummary, 'conversationId' | 'messageId'>
): string | null {
  if (c.conversationId === null) return null;
  const base = `/admin/neo/sessions/${encodeURIComponent(c.conversationId)}`;
  return c.messageId === null ? base : `${base}?turn=${encodeURIComponent(c.messageId)}`;
}

/** The audited refusals list narrowed to one refusal candidate (TOM-262). */
export function refusalHref(c: Pick<EvalCandidateSummary, 'refusalId'>): string | null {
  return c.refusalId === null ? null : `/admin/neo/refusals?id=${encodeURIComponent(c.refusalId)}`;
}

/** Neo → Gate narrowed to the candidate's disagreement (TOM-303, TOM-314). */
export function gateHref(c: Pick<EvalCandidateSummary, 'gateDisagreementId'>): string | null {
  return c.gateDisagreementId === null
    ? null
    : `/admin/neo/gate?id=${encodeURIComponent(c.gateDisagreementId)}`;
}

// ----- Labels -------------------------------------------------------------------------------

const SOURCE_LABEL: Record<EvalCandidateSource, string> = {
  thumbs_down: 'Thumbs down',
  output_gate: 'Output gate',
  review_tag: 'Review tag',
  input_refusal: 'Input refusal',
  gate_disagreement: 'Gate disagreement',
  promoted: 'Promoted',
};

export function sourceLabel(source: EvalCandidateSource): string {
  return SOURCE_LABEL[source];
}

const STATE_LABEL: Record<EvalTriageState, string> = {
  new: 'New',
  drafting: 'Drafting',
  accepted: 'Accepted',
  rejected: 'Rejected',
  duplicate: 'Duplicate',
  in_corpus: 'In corpus',
};

export function stateLabel(state: EvalTriageState): string {
  return STATE_LABEL[state];
}

export type InboxTone = 'succeeded' | 'failed' | 'running' | 'neutral' | 'warn' | 'muted';

export function stateTone(state: EvalTriageState): InboxTone {
  switch (state) {
    case 'new':
      return 'running';
    case 'drafting':
      return 'warn';
    case 'accepted':
    case 'in_corpus':
      return 'succeeded';
    case 'rejected':
    case 'duplicate':
      return 'muted';
  }
}

const KIND_LABEL: Record<EvalCaseKind, string> = {
  attack: 'Attack',
  helpful: 'Helpful',
  owner_question: 'Owner question',
};

export function kindLabel(kind: string | null): string {
  if (kind === null) return '—';
  return (KIND_LABEL as Record<string, string>)[kind] ?? kind;
}

/**
 * An agent's name as the console gives it: Neo, or an expert by the Evals
 * guide's name for it (guide.ts), so the two never differ. A candidate with
 * none chosen is Neo's.
 */
export function targetAgentLabel(target: string | null): string {
  return target === null || target === 'chat' ? 'Neo' : expertName(target);
}

/**
 * What each agent does, in the draft form's words: for an expert, the first
 * sentence of the guide's words for it (guide.ts EXPERT_TEXT).
 */
export function targetAgentHint(target: EvalCaseTargetAgent): string {
  if (target === 'chat') {
    return 'Neo itself: it asked the wrong expert or none, or told the answer wrong.';
  }
  const what = EXPERT_TEXT[target]?.what ?? '';
  const end = what.indexOf('. ');
  return end < 0 ? what : what.slice(0, end + 1);
}

/**
 * The experts that took part in a turn, by name (ADR 0342 §9), or "None". A
 * turn stores no list when none did.
 */
export function turnExpertsText(specialists: readonly string[] | null): string {
  return specialists === null || specialists.length === 0
    ? 'None'
    : specialists.map(targetAgentLabel).join(', ');
}

/**
 * What a case for each expert is tagged with when it is imported, and the
 * category every one of its questions takes (the contract's EVAL_CASE_EXPERTS).
 */
export const EXPERT_CASE: Record<
  Exclude<EvalCaseTargetAgent, 'chat'>,
  { domain: string; category: string }
> = {
  boards: { domain: 'boards', category: 'board' },
  electrical: { domain: 'electrical', category: 'electrical' },
  mechanical: { domain: 'mechanical', category: 'mechanical' },
  electronics: { domain: 'electronics', category: 'electronics' },
  navigation: { domain: 'navigation', category: 'navigation' },
};

/**
 * The expert a category belongs to (the contract's evalCaseExpertOfCategory):
 * the corpus tags a case of these categories with the expert's domain, so
 * only a case for that expert may take one. `dashboard` is the boards' too.
 */
const CATEGORY_EXPERT: Record<string, Exclude<EvalCaseTargetAgent, 'chat'>> = {
  board: 'boards',
  dashboard: 'boards',
  electrical: 'electrical',
  electronics: 'electronics',
  mechanical: 'mechanical',
  navigation: 'navigation',
};

export function expertOfCategory(
  category: string
): Exclude<EvalCaseTargetAgent, 'chat'> | undefined {
  return Object.hasOwn(CATEGORY_EXPERT, category) ? CATEGORY_EXPERT[category] : undefined;
}

/** An expert behind Neo, not Neo itself (TOM-533). */
export function isExpertAgent(
  target: string | null | undefined
): target is Exclude<EvalCaseTargetAgent, 'chat'> {
  return target != null && target !== 'chat' && Object.hasOwn(EXPERT_CASE, target);
}

/**
 * Whether the draft may give the case to an expert: never a gate case, which
 * is the input gate's, nor an input-gate refusal, which reached no agent.
 */
export function expertsOffered(sources: readonly EvalCandidateSource[]): boolean {
  return !sources.includes('gate_disagreement') && !sources.includes('input_refusal');
}

/** "Turn 1a2b3c4d", "Refusal …", "Gate disagreement …", "Promoted case …": a candidate's name in a list. */
export function candidateTitle(c: Pick<EvalCandidateSummary, 'key'>): string {
  const [kind, id = ''] = c.key.split(':');
  const short = id.slice(0, 8);
  if (kind === 'turn') return `Turn ${short}`;
  if (kind === 'refusal') return `Refusal ${short}`;
  if (kind === 'gate') return `Gate disagreement ${short}`;
  return `Promoted case ${short}`;
}

/** Whether a candidate's case goes to the input gate's labelled set (TOM-314). */
export function isGateCandidate(sources: readonly EvalCandidateSource[]): boolean {
  return sources.includes('gate_disagreement');
}

const GATE_OUTCOME_TEXT: Record<GateOutcome, string> = {
  allow: 'allowed',
  safety: 'refused (safety)',
  security: 'refused (security)',
};

/** "Jev allowed; the regex refused (security)." */
export function gateOutcomeText(
  g: Pick<EvalGateDisagreementInfo, 'jevOutcome' | 'regexOutcome'>
): string {
  return `Jev ${GATE_OUTCOME_TEXT[g.jevOutcome]}; the regex ${GATE_OUTCOME_TEXT[g.regexOutcome]}.`;
}

/** Jev's scores in the gate's question order, as the page lists them. */
export function gateScores(
  g: Pick<EvalGateDisagreementInfo, 'jevScores'>
): Array<{ question: EvalGateQuestion; score: number | null }> {
  return EVAL_GATE_QUESTIONS.map((question) => {
    const v = g.jevScores[question];
    return { question, score: typeof v === 'number' ? v : null };
  });
}

/** Each source a candidate has, with its signal count and the enumerated details. */
export function candidateSignals(
  c: Pick<EvalCandidateSummary, 'sources' | 'signals' | 'details'>
): Array<{ source: EvalCandidateSource; label: string; count: number; details: string[] }> {
  const detailsOf: Record<EvalCandidateSource, string[]> = {
    thumbs_down: c.details.feedbackReasons,
    output_gate: c.details.outputGates,
    review_tag: c.details.reviewTags,
    input_refusal: c.details.refusalReasons,
    gate_disagreement: c.details.gateOutcomes,
    promoted: c.details.promotedCaseTypes,
  };
  return c.sources.map((source) => ({
    source,
    label: SOURCE_LABEL[source],
    count: c.signals[source],
    details: detailsOf[source],
  }));
}

/**
 * The latest stored outcome of a case in the corpus, in words. A claims
 * verdict leads; a short case without one reads whether the reply declined,
 * which is a pass for an attack and a fail for a helpful case, so it is shown
 * without a tone of its own.
 */
export function inCorpusOutcome(inCorpus: EvalCandidateInCorpus): {
  text: string;
  tone: InboxTone;
} {
  const l = inCorpus.latest;
  const local = l.localDecision !== null ? ' by the policy layer' : '';
  if (l.errored) return { text: 'Errored', tone: 'failed' };
  if (l.blocked !== null) return { text: `Blocked (${l.blocked})`, tone: 'warn' };
  if (l.met !== null) {
    return l.met
      ? { text: 'Claims met', tone: 'succeeded' }
      : { text: 'Claims not met', tone: 'failed' };
  }
  if (l.declined !== null) {
    return { text: `${l.declined ? 'Declined' : 'Answered'}${local}`, tone: 'neutral' };
  }
  if (l.localDecision !== null) {
    return {
      text: `${l.localDecision === 'refused' ? 'Refused' : 'Answered'}${local}`,
      tone: 'neutral',
    };
  }
  return { text: 'Not graded yet', tone: 'muted' };
}

const MOMENT_REASON_TEXT: Record<Extract<EvalMomentOption, { offered: false }>['reason'], string> =
  {
    not_a_turn:
      'An input-gate refusal never reached the model, so it has no turn to take a moment from.',
    turn_erased: "The turn was erased (its owner's account was deleted).",
    no_boat: "The turn's conversation was about no boat.",
    no_consent: "The turn's boat has no active recorded-moment consent.",
    lookup_failed: 'The consent list could not be read, which counts as no consent.',
  };

/** What the draft form says about a recorded moment for this candidate. */
export function momentOptionText(option: EvalMomentOption): {
  offered: boolean;
  text: string;
  warning: string | null;
} {
  if (!option.offered) {
    return {
      offered: false,
      text: `Stub boat only. ${MOMENT_REASON_TEXT[option.reason]}`,
      warning: null,
    };
  }
  return {
    offered: true,
    text: `The boat has an active consent under the corpus key ${option.boat}: a recorded moment at ${formatDate(option.at)} (${option.momentId}) is allowed.`,
    warning: option.windowHeld
      ? null
      : 'The store holds no telemetry and no fix for this boat in the 48 hours before the turn. The import would refuse this moment: draft the case on the stub boat.',
  };
}

const REVOKED_REASON_TEXT: Record<EvalConsentRevokeReason, string> = {
  withdrawn: 'Withdrawn by the owner',
  account_deleted: "The owner's account was deleted",
  boat_deleted: 'The boat was deleted',
};

export function revokedReasonText(reason: EvalConsentRevokeReason | null): string {
  return reason === null ? 'Reason not recorded' : REVOKED_REASON_TEXT[reason];
}

// ----- The draft form -------------------------------------------------------------------------

/** The reviewer's form. Text as typed; the bodies below trim and drop what is empty. */
export interface DraftForm {
  slug: string;
  prompt: string;
  kind: EvalCaseKind | '';
  category: string;
  must: string[];
  mustNot: string[];
  dataSource: 'stub_boat' | 'moment';
  noPositions: boolean;
  expect: EvalCaseExpect | '';
  expectedTools: string;
  /** A case from a gate disagreement, for the input gate's labelled set (TOM-314). */
  gate: boolean;
  /** Gate set, expect refuse: the gate questions that may refuse it. */
  gateQuestions: EvalGateQuestion[];
  note: string;
  /** Which agent should get it right (TOM-533, ADR 0342 §9). A gate case sends none. */
  targetAgent: EvalCaseTargetAgent;
  /**
   * The category typed for Neo, kept while an expert's fills the field, so
   * going back to Neo gives it back.
   */
  neoCategory: string;
}

export const CLAIMS_MAX = 12;
export const PROMPT_MAX = 4000;
const CATEGORY = /^[a-z][a-z0-9_]*$/;
const TOOL = /^[a-z][a-z0-9_]*$/;

function asKind(v: string | null): EvalCaseKind | '' {
  return v !== null && (EVAL_CASE_KINDS as readonly string[]).includes(v)
    ? (v as EvalCaseKind)
    : '';
}

function asExpect(v: string | null): EvalCaseExpect | '' {
  return v !== null && (EVAL_CASE_EXPECTS as readonly string[]).includes(v)
    ? (v as EvalCaseExpect)
    : '';
}

/**
 * The form for a candidate, from its stored draft. A promoted row's prompt is
 * already reviewer-authored and pre-fills it (ADR 0233 §4); the server never
 * copies chat text into a draft. Each claim list starts with one empty line.
 */
export function draftFormFrom(
  detail: Pick<EvalCandidateDetail, 'draft'> & { sources?: readonly EvalCandidateSource[] }
): DraftForm {
  const d = detail.draft;
  const must = d.claims?.must ?? [];
  const mustNot = d.claims?.mustNot ?? [];
  const base: DraftForm = {
    slug: '',
    prompt: d.prompt ?? '',
    kind: asKind(d.kind),
    category: d.category ?? '',
    must: must.length > 0 ? [...must] : [''],
    mustNot: mustNot.length > 0 ? [...mustNot] : [''],
    dataSource: d.data?.source === 'moment' ? 'moment' : 'stub_boat',
    noPositions: d.data?.source === 'stub_boat' && d.data.variant === 'noPositions',
    expect: asExpect(d.expect),
    expectedTools: (d.expectedTools ?? []).join(', '),
    gate: isGateCandidate(detail.sources ?? []),
    gateQuestions: EVAL_GATE_QUESTIONS.filter((q) => d.gateQuestions?.includes(q) ?? false),
    note: d.note ?? '',
    targetAgent: 'chat',
    neoCategory: d.category ?? '',
  };
  // The agent: Neo where no expert may be chosen, and an expert's own category
  // with a stored expert, as the form keeps it.
  const stored = oneOf(EVAL_CASE_TARGET_AGENTS, d.targetAgent);
  if (isExpertAgent(stored) && expertsOffered(detail.sources ?? [])) {
    return withTargetAgent({ ...base, category: '' }, stored);
  }
  return base;
}

/**
 * The form with another agent chosen. An expert's questions all take its
 * category, so choosing one fills it in and keeps what was typed for Neo;
 * going back to Neo gives that back.
 */
export function withTargetAgent(form: DraftForm, targetAgent: EvalCaseTargetAgent): DraftForm {
  const neoCategory = isExpertAgent(form.targetAgent) ? form.neoCategory : form.category;
  return isExpertAgent(targetAgent)
    ? { ...form, targetAgent, category: EXPERT_CASE[targetAgent].category, neoCategory }
    : { ...form, targetAgent, category: neoCategory, neoCategory };
}

function cleanClaims(lines: readonly string[]): string[] {
  return lines.map((l) => l.trim()).filter((l) => l.length > 0);
}

/** The tool names typed into the form, split on commas and spaces. */
export function parseTools(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/** The case data the form names. A moment takes the server's offer: its key and minute. */
export function draftData(form: DraftForm, option: EvalMomentOption): EvalCaseData {
  if (form.dataSource === 'moment' && option.offered) {
    return { source: 'moment', boat: option.boat, at: option.at };
  }
  return form.noPositions
    ? { source: 'stub_boat', variant: 'noPositions' }
    : { source: 'stub_boat' };
}

/**
 * PUT .../draft: the whole form, trimmed. A save is the whole form, so a field
 * left empty here is cleared on the server. The slug goes only with the first
 * save, which assigns the case id. A gate case sends no claims, no data
 * variant and no expected tools, and its questions only with expect refuse.
 */
export function draftSaveBody(
  form: DraftForm,
  option: EvalMomentOption,
  hasCaseId: boolean
): EvalDraftSaveBody {
  const body: EvalDraftSaveBody = {
    data: form.gate ? { source: 'stub_boat' } : draftData(form, option),
  };
  const slug = form.slug.trim();
  if (!hasCaseId && slug.length > 0) body.slug = slug;
  const prompt = form.prompt.trim();
  if (prompt.length > 0) body.prompt = prompt;
  if (form.kind !== '') body.kind = form.kind;
  const category = form.category.trim();
  if (category.length > 0) body.category = category;
  if (form.gate) {
    if (form.expect !== '') body.expect = form.expect;
    if (form.expect === 'refuse' && form.gateQuestions.length > 0) {
      body.gateQuestions = [...form.gateQuestions];
    }
    const note = form.note.trim();
    if (note.length > 0) body.note = note;
    return body;
  }
  const must = cleanClaims(form.must);
  const mustNot = cleanClaims(form.mustNot);
  if (must.length + mustNot.length > 0) body.claims = { must, mustNot };
  if (form.expect !== '') body.expect = form.expect;
  const tools = parseTools(form.expectedTools);
  if (tools.length > 0) body.expectedTools = tools;
  const note = form.note.trim();
  if (note.length > 0) body.note = note;
  body.targetAgent = form.targetAgent;
  return body;
}

/** The accept decision: the complete draft with the row's case id. */
export function acceptDecision(
  form: DraftForm,
  option: EvalMomentOption,
  caseId: string
): EvalTriageDecision {
  const body = draftSaveBody(form, option, true);
  const draft: EvalCaseDraft = {
    caseId,
    kind: body.kind ?? 'helpful',
    category: body.category ?? '',
    prompt: body.prompt ?? '',
    data: body.data ?? { source: 'stub_boat' },
  };
  if (!form.gate) draft.claims = body.claims ?? { must: [], mustNot: [] };
  if (body.expect !== undefined) draft.expect = body.expect;
  if (body.expectedTools !== undefined) draft.expectedTools = body.expectedTools;
  if (body.gateQuestions !== undefined) draft.gateQuestions = body.gateQuestions;
  if (body.note !== undefined) draft.note = body.note;
  if (body.targetAgent !== undefined) draft.targetAgent = body.targetAgent;
  return { to: 'accepted', draft };
}

/**
 * Why the form cannot be accepted yet, in the contract's terms (ADR 0233 §4,
 * §8; ADR 0301 for a gate case). Empty when it can. `sources` are the
 * candidate's: a case from an input-gate refusal must assert what the gate
 * does.
 */
export function draftProblems(
  form: DraftForm,
  ctx: { sources: readonly EvalCandidateSource[]; option: EvalMomentOption }
): string[] {
  const problems: string[] = [];
  const prompt = form.prompt.trim();
  if (prompt.length === 0) problems.push('Write the prompt.');
  if (prompt.length > PROMPT_MAX) problems.push(`The prompt is over ${PROMPT_MAX} characters.`);
  if (form.kind === '') problems.push('Pick the kind.');
  const category = form.category.trim();
  if (category.length === 0) problems.push('Name the category.');
  else if (!CATEGORY.test(category) || category.length > 40) {
    problems.push('The category is snake_case: lowercase letters, digits and underscores.');
  }
  if (form.gate) {
    if (form.expect !== 'allow' && form.expect !== 'refuse') {
      problems.push('A gate case says what the input gate should do: expect allow or refuse.');
    } else if (form.expect === 'refuse' && form.gateQuestions.length === 0) {
      problems.push('Tick the gate questions that may refuse it.');
    }
    if (form.note.trim().length > 500) problems.push('The note is over 500 characters.');
    return problems;
  }

  const must = cleanClaims(form.must);
  const mustNot = cleanClaims(form.mustNot);
  if (must.length + mustNot.length === 0) problems.push('Write at least one claim.');
  if (must.length > CLAIMS_MAX || mustNot.length > CLAIMS_MAX) {
    problems.push(`At most ${CLAIMS_MAX} claims of each kind.`);
  }
  const all = [...must, ...mustNot];
  if (all.some((c) => c.length < 3 || c.length > 300)) {
    problems.push('Each claim is 3 to 300 characters.');
  }
  const seen = new Set<string>();
  for (const c of all) {
    const norm = c.toLowerCase();
    if (seen.has(norm)) {
      problems.push(`A claim repeats: ${c}`);
      break;
    }
    seen.add(norm);
  }

  const tools = parseTools(form.expectedTools);
  if (tools.length > 8) problems.push('At most 8 expected tools.');
  if (tools.some((t) => !TOOL.test(t))) problems.push('A tool name is snake_case.');
  if (form.note.trim().length > 500) problems.push('The note is over 500 characters.');

  const fromRefusal = ctx.sources.includes('input_refusal');
  if (form.dataSource === 'moment') {
    if (!ctx.option.offered) problems.push('A recorded moment is not allowed for this candidate.');
    if (form.kind !== '' && form.kind !== 'owner_question') {
      problems.push('A recorded moment backs only an owner question.');
    }
    if (fromRefusal) problems.push('A case from an input-gate refusal runs on the stub boat.');
    if (form.expect !== '') problems.push('Expect is a short-corpus field: clear it for a moment.');
    if (tools.length > 0) problems.push('Expected tools are a short-corpus field.');
  } else if (fromRefusal && (form.expect === '' || form.expect === 'model')) {
    problems.push(
      'A case from an input-gate refusal says what the gate should do: expect refuse, allow or local-answer.'
    );
  }

  // The agent (TOM-533, ADR 0342 §9), as the contract routes it.
  if (isExpertAgent(form.targetAgent)) {
    const name = targetAgentLabel(form.targetAgent);
    const expert = EXPERT_CASE[form.targetAgent];
    if (fromRefusal) problems.push('An input-gate refusal never reached an expert: it is for Neo.');
    if (form.kind === 'attack') {
      problems.push('An attack is for Neo: every attack case is already tried on each expert.');
    }
    if (form.dataSource === 'moment') {
      problems.push(
        "An expert's own questions use the made-up boat for now. Pick Neo, or switch to the made-up boat."
      );
    }
    if (form.expect === 'refuse' || form.expect === 'local-answer') {
      problems.push(
        'An expert never sees a message the input gate refuses or answers itself: expect allow or model.'
      );
    }
    if (category.length > 0 && category !== expert.category) {
      problems.push(`A case for ${name} takes the category ${expert.category}.`);
    }
  } else {
    const owner = expertOfCategory(category);
    if (owner !== undefined) {
      const name = targetAgentLabel(owner);
      problems.push(
        fromRefusal
          ? `This category belongs to ${name}, and a refused message is Neo's: pick another category.`
          : `This category belongs to ${name}: choose ${name}, or pick another category.`
      );
    }
  }
  return problems;
}

/** Where an accepted case goes and how it is split (ADR 0233 §8, §9). */
export function routePreview(
  form: DraftForm,
  option: EvalMomentOption,
  storedSplit: 'train' | 'test' | null,
  /** The saved expert case's split among the expert's own questions, when the form keeps its agent. */
  expertSplit: 'train' | 'test' | null = null
): { corpus: string; split: string } {
  if (form.gate) {
    const expect =
      form.expect === 'refuse'
        ? `expect refuse${form.gateQuestions.length > 0 ? ` by ${form.gateQuestions.join(', ')}` : ''}`
        : form.expect === 'allow'
          ? 'expect allow'
          : 'expect allow or refuse';
    return {
      corpus: `The input gate's labelled set (INBOX_GATE_CASES), ${expect}. The gate calibration (pnpm neo:eval:gate) scores it with Jev; Tier 1 does not run it.`,
      split: 'None: the calibration reads every case',
    };
  }
  // A case for an expert also becomes one of the questions that expert is
  // tested on alone (TOM-533, ADR 0342 §9); only a made-up-boat case can be one.
  const expert =
    isExpertAgent(form.targetAgent) && form.dataSource !== 'moment'
      ? ` It also becomes one of the questions ${targetAgentLabel(form.targetAgent)} is tested on alone, without Neo.`
      : '';
  if (form.dataSource === 'moment' && option.offered) {
    return {
      corpus: `Long corpus (NEO_EVAL_LONG_QUESTIONS), replaying moment ${option.momentId}. Graded by its claims.${expert}`,
      split:
        storedSplit !== null
          ? `${storedSplit}, by a seeded hash of the case id`
          : 'Train or test, by a seeded hash of the case id once the draft is saved',
    };
  }
  const kind = form.kind === 'attack' ? 'attack' : 'helpful';
  const expect = form.expect === '' ? 'model' : form.expect;
  const variant = form.noPositions ? ', without positions' : '';
  return {
    corpus: `Short corpus (NEO_EVAL_CORPUS) as ${kind}, expect ${expect}, on the stub boat SV Evaluator${variant}.${expert}`,
    split: isExpertAgent(form.targetAgent)
      ? `${expertSplit === null ? 'Train or test' : expertSplit} among the questions ${targetAgentLabel(form.targetAgent)} is tested on alone, by a seeded hash of the case id${expertSplit === null ? ' once the draft is saved' : ''}. Neo's short suite is not split.`
      : 'None: the short suite is a regression gate, and every case in it must hold',
  };
}

/**
 * The case id the first save will assign, with the random suffix shown as
 * `xxxxxx`: `inbox-<slug>-<6 hex>`, from the slug, else the category, else the
 * first source, as the server picks it.
 */
export function caseIdPreview(form: DraftForm, firstSource: EvalCandidateSource | null): string {
  const raw =
    form.slug.trim() || form.category.trim() || (firstSource !== null ? firstSource : 'case');
  const room = 64 - 'inbox-'.length - '-'.length - 6;
  const words = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, room)
    .replace(/-+$/, '');
  return `inbox-${words.length > 0 ? words : 'case'}-xxxxxx`;
}

// ----- Triage ---------------------------------------------------------------------------------

const CANDIDATE_KEY =
  /^(?:turn|refusal|gate|promoted):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const CASE_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * What a duplicate points at, from what the reviewer typed: another
 * candidate's key, or a case id the corpus, the inbox or a stored run holds.
 * Null when it is neither.
 */
export function parseDuplicateTarget(
  text: string
): { candidate: string } | { caseId: string } | null {
  const t = text.trim();
  const lower = t.toLowerCase();
  if (CANDIDATE_KEY.test(lower)) return { candidate: lower };
  if (t.length > 0 && t.length <= 64 && CASE_ID.test(t)) return { caseId: t };
  return null;
}

const MOVE_LABEL: Record<EvalTriageState, string> = {
  new: 'Back to new',
  drafting: 'Draft it',
  accepted: 'Accept',
  rejected: 'Reject…',
  duplicate: 'Mark a duplicate…',
  in_corpus: 'In corpus',
};

/** The button text for a move, from the candidate's state. */
export function moveLabel(from: EvalTriageState, to: EvalTriageState): string {
  if (from === 'accepted' && to === 'drafting') return 'Back to drafting';
  return MOVE_LABEL[to];
}

// ----- The export -----------------------------------------------------------------------------

/** The download's file name: the deployment and the export time, safe for any file system. */
export function exportFileName(file: Pick<EvalCaseExportFile, 'deployEnv' | 'exportedAt'>): string {
  const stamp = file.exportedAt.replace(/\.\d{3}Z$/, 'Z').replace(/:/g, '-');
  return `tombolo-neo-eval-cases-${file.deployEnv}-${stamp}.json`;
}

// ----- Consent ----------------------------------------------------------------------------------

/**
 * What a grant covers (ADR 0233 §5 as amended, §6): no column restates it, so
 * the console says it where a grant is recorded.
 */
export const CONSENT_SCOPE =
  "A recorded moment is a copy of the boat's data up to the turn's minute, committed to the repository: its telemetry and that telemetry's history, the engine logbook and voyage log derived from it, the boat's name and vessel type, the names of its devices, its last fix at or before that minute as its position, and its track, every fix the store holds for it up to then.";

/** A corpus key: lowercase letters and digits, starting with a letter, at most 32. */
export function corpusKeyProblem(key: string): string | null {
  if (key.length === 0) return 'Name the corpus key.';
  if (key.length > 32) return 'A corpus key is at most 32 characters.';
  if (!/^[a-z][a-z0-9]*$/.test(key)) {
    return 'A corpus key is lowercase letters and digits, starting with a letter.';
  }
  return null;
}

/** A corpus key suggested from a boat's name: `AnchorTest` → `anchortest`. */
export function suggestCorpusKey(boatName: string | null): string {
  const key = (boatName ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/^[0-9]+/, '')
    .slice(0, 32);
  return key;
}

/**
 * The basis the form starts from: how the owner agreed, naming the position
 * and the track, which the grant covers (the admin guide, PR #112). The admin
 * edits it to say how this owner agreed.
 */
export function consentBasisTemplate(input: {
  ownerEmail: string;
  boatName: string;
  date: string;
}): string {
  return `${input.ownerEmail}, the owner of ${input.boatName}, agreed on ${input.date} to recorded moments from this boat in the Neo eval corpus: its telemetry up to each turn's minute, its position at that minute and its track up to then.`;
}

/** Whether a basis names the position and the track, as the grant covers both. */
export function basisNamesPositionAndTrack(basis: string): boolean {
  const b = basis.toLowerCase();
  return /\bposition/.test(b) && /\btrack/.test(b);
}

/** The basis's problems, or empty when it can be recorded. */
export function basisProblems(basis: string): string[] {
  const b = basis.trim();
  const problems: string[] = [];
  if (b.length < 3) problems.push('Say how the owner agreed.');
  if (b.length > 500) problems.push('The basis is at most 500 characters.');
  if (b.length >= 3 && !basisNamesPositionAndTrack(b)) {
    problems.push(
      'Name the position and the track: the grant covers both, and the basis records that the owner agreed to them.'
    );
  }
  return problems;
}

/** Each boat's owners as people the consent can name: the owner emails matched to user ids. */
export function boatOwners(
  boat: Pick<AdminBoatRow, 'owner_emails'>,
  users: readonly Pick<AdminUserRow, 'id' | 'email'>[]
): Array<{ userId: string; email: string }> {
  const byEmail = new Map(users.map((u) => [u.email.toLowerCase(), u]));
  return boat.owner_emails.flatMap((email) => {
    const user = byEmail.get(email.toLowerCase());
    return user === undefined ? [] : [{ userId: user.id, email: user.email }];
  });
}

/** A boat's name in the consent list: its display name, its short id, or that it was deleted. */
export function consentBoatText(
  boatId: string | null,
  boats: readonly Pick<AdminBoatRow, 'id' | 'display_name'>[]
): string {
  if (boatId === null) return 'Boat deleted';
  const boat = boats.find((b) => b.id === boatId);
  const name = boat?.display_name?.trim();
  return name !== undefined && name.length > 0 ? name : `Boat ${boatId.slice(0, 8)}`;
}

/** A person's email for an id the consent list names, else its short id, or that the account is gone. */
export function consentUserText(
  userId: string | null,
  users: readonly Pick<AdminUserRow, 'id' | 'email'>[],
  gone = 'Account deleted'
): string {
  if (userId === null) return gone;
  return users.find((u) => u.id === userId)?.email ?? `User ${userId.slice(0, 8)}`;
}
